import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { pickDetail } from '../lib/data'
import { PROGRAM_COLOR } from './Programs'

const TIER_STYLE = {
  'Critical': { bg: '#FCEBEB', color: '#A32D2D', dot: '🔴' },
  'High':     { bg: '#FAEEDA', color: '#854F0B', dot: '🟡' },
  'Medium':   { bg: '#EAF3DE', color: '#3B6D11', dot: '🟢' },
  'Low':      { bg: '#F1EFE8', color: '#5F5E5A', dot: '⚪' },
  'AMP':      { bg: '#F3E8FF', color: '#6B21A8', dot: '🟣' },
}

const STATUS_STYLE = {
  'open':        { bg: '#FCEBEB', color: '#A32D2D' },
  'scheduled':   { bg: '#F1EFE8', color: '#5F5E5A' },
  'dispatched':  { bg: '#FAEEDA', color: '#854F0B' },
  'remote':      { bg: '#E6F1FB', color: '#185FA5' },
  'in_progress': { bg: '#FAEEDA', color: '#854F0B' },
  'blocked':     { bg: '#FCEBEB', color: '#A32D2D' },
  'resolved':    { bg: '#EAF3DE', color: '#3B6D11' },
  'cancelled':   { bg: '#F1EFE8', color: '#5F5E5A' },
}

export default function ProgramDetail() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const [program, setProgram] = useState(null)
  const [programSites, setProgramSites] = useState([])
  const [tickets, setTickets] = useState([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab] = useState('overview')
  const [tierFilter, setTierFilter] = useState('all')
  const [allSites, setAllSites] = useState([]) // for the Link sites modal
  const [showLinkModal, setShowLinkModal] = useState(false)

  useEffect(() => { fetchAll() }, [slug])

  async function fetchAll() {
    setLoading(true)
    const { data: prog } = await supabase.from('programs').select('*').eq('slug', slug).single()
    if (!prog) { setLoading(false); return }

    const [psRes, ticketsRes] = await Promise.all([
      supabase.from('program_sites')
        .select('*, sites(id, name, city, state, site_details(priority_tier, priority_score, lpr_issue, gate_issue, site_code, district))')
        .eq('program_id', prog.id),
      supabase.from('tickets')
        .select('*, sites(name)')
        .eq('program_id', prog.id)
        .order('priority_rank', { ascending: false })
        .order('created_at',    { ascending: false }),
    ])
    setProgram(prog)
    setProgramSites(psRes.data || [])
    setTickets(ticketsRes.data || [])

    // Load all active sites once so the Link Sites modal can show candidates.
    const { data: sitesData } = await supabase
      .from('sites').select('id, name, city, state').eq('active', true).order('name')
    setAllSites(sitesData || [])

    setLoading(false)
  }

  async function unlinkSite(programSiteId, siteName) {
    if (!window.confirm(`Remove "${siteName}" from this program?\n\nTickets already created for this site will stay, but the site will no longer appear in this program.`)) return
    const { error } = await supabase.from('program_sites').delete().eq('id', programSiteId)
    if (error) { alert('Unlink failed: ' + error.message); return }
    fetchAll()
  }

  async function linkSites(siteIds, opts) {
    if (!program || siteIds.length === 0) return
    const rows = siteIds.map(site_id => ({
      program_id: program.id,
      site_id,
      hardware_cost_cents: opts.hardware_cost_cents || 0,
      cutover_wave: opts.cutover_wave || null,
    }))
    const { error } = await supabase.from('program_sites').insert(rows)
    if (error) { alert('Link failed: ' + error.message); return }
    setShowLinkModal(false)
    fetchAll()
  }

  if (loading) return <div className="spinner" />
  if (!program) return <div className="empty-state"><h3>Program not found</h3></div>

  const color = PROGRAM_COLOR[program.color] || PROGRAM_COLOR.slate
  const openCount = tickets.filter(t => !['resolved','cancelled'].includes(t.status)).length
  const critOpen  = tickets.filter(t => t.tier === 'Critical' && !['resolved','cancelled'].includes(t.status)).length
  const resolved  = tickets.filter(t => t.status === 'resolved').length

  // PA Phase 1 hardware stats
  const hwSites      = programSites.filter(ps => ps.hardware_cost_cents > 0)
  const hwTotal      = hwSites.reduce((a, ps) => a + (ps.hardware_cost_cents || 0), 0)
  const hwComplete   = hwSites.filter(ps => ps.hardware_install_status === 'complete').length
  const hwBudgetSpent = hwSites.filter(ps => ps.hardware_install_status === 'complete')
    .reduce((a, ps) => a + (ps.hardware_cost_cents || 0), 0)

  // AMP wave grouping
  const waves = {}
  if (slug === 'amp-migration') {
    programSites.forEach(ps => {
      const w = ps.cutover_wave || 0
      if (!waves[w]) waves[w] = []
      waves[w].push(ps)
    })
  }

  // tier-filtered sites
  const filteredSites = programSites.filter(ps => {
    if (tierFilter === 'all') return true
    const tier = pickDetail(ps.sites?.site_details)?.priority_tier
    return tier === tierFilter || (tierFilter === 'AMP' && slug === 'amp-migration')
  })

  return (
    <div>
      <button
        className="btn btn-sm"
        style={{ marginBottom: 12 }}
        onClick={() => navigate('/programs')}
      >
        ← Back to programs
      </button>

      <div style={{ marginBottom: 16 }}>
        <span style={{
          display: 'inline-block', padding: '4px 12px', borderRadius: 16,
          background: color.solid, color: 'white', fontSize: 12, fontWeight: 600,
          marginBottom: 8,
        }}>
          {program.slug === 'pa-phase1' ? 'PA PHASE 1' : program.slug === 'amp-migration' ? 'AMP MIGRATION' : 'MAIN PROGRAM'}
        </span>
        <div className="page-title">{program.name}</div>
        <div className="page-subtitle" style={{ marginTop: 4 }}>{program.description}</div>
      </div>

      <div className="stats-grid">
        <div className="stat-card"><div className="stat-number">{programSites.length}</div><div className="stat-label">Sites</div></div>
        <div className="stat-card"><div className="stat-number">{openCount}</div><div className="stat-label">Open tickets</div></div>
        <div className="stat-card"><div className="stat-number" style={{ color: critOpen > 0 ? 'var(--red)' : undefined }}>{critOpen}</div><div className="stat-label">Critical open</div></div>
        <div className="stat-card"><div className="stat-number" style={{ color: 'var(--green)' }}>{resolved}</div><div className="stat-label">Resolved</div></div>
      </div>

      {/* PA Phase 1 — Hardware investment tracker */}
      {slug === 'pa-phase1' && hwSites.length > 0 && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>Hardware investment tracker</div>
            <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {hwComplete} of {hwSites.length} complete
            </div>
          </div>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 12 }}>
            ${(hwBudgetSpent / 100).toLocaleString()} of ${(hwTotal / 100).toLocaleString()} committed
          </div>
          <div style={{ display: 'flex', gap: 2, height: 28, borderRadius: 6, overflow: 'hidden' }}>
            {hwSites.map(ps => {
              const s = ps.hardware_install_status
              const fill = s === 'complete' ? 'var(--green)'
                : s === 'in_progress' ? 'var(--blue-mid)'
                : s === 'scheduled'   ? '#D3D1C7'
                : '#EDEAE1'
              return (
                <div key={ps.id} style={{ flex: 1, background: fill }}
                  title={`${ps.sites?.name} — ${s}`} />
              )
            })}
          </div>
          <div style={{ display: 'flex', gap: 12, fontSize: 11, color: 'var(--text-muted)', marginTop: 8 }}>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, background: 'var(--green)', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />Complete</span>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, background: 'var(--blue-mid)', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />In progress</span>
            <span><span style={{ display: 'inline-block', width: 10, height: 10, background: '#D3D1C7', borderRadius: 2, marginRight: 4, verticalAlign: 'middle' }} />Pending</span>
          </div>
        </div>
      )}

      {/* AMP — Cutover waves */}
      {slug === 'amp-migration' && (
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Cutover waves</div>
          {Object.keys(waves).sort().map(wave => (
            <div key={wave} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 6 }}>
                Wave {wave} — {waves[wave].length} sites
              </div>
              {waves[wave].map(ps => (
                <div key={ps.id}
                  className="site-item"
                  style={{ marginBottom: 4 }}
                  onClick={() => navigate(`/sites/${ps.sites?.id || ''}`)}>
                  <div style={{ flex: 1 }}>
                    <div className="site-name">{ps.sites?.name}</div>
                    <div className="site-meta">
                      {ps.sites?.city} · Cutover {formatDate(ps.cutover_date)}
                    </div>
                  </div>
                  <span className="badge badge-info">{ps.cutover_status}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Tabs */}
      <div className="tabs" style={{ marginTop: 16 }}>
        <button className={`tab-btn ${tab === 'overview' ? 'active' : ''}`} onClick={() => setTab('overview')}>Sites</button>
        <button className={`tab-btn ${tab === 'tickets' ? 'active' : ''}`} onClick={() => setTab('tickets')}>
          Tickets ({tickets.length})
        </button>
      </div>

      {tab === 'overview' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {slug !== 'amp-migration' && ['all','Critical','High','Medium','Low'].map(t => (
                <button
                  key={t}
                  className={`btn btn-sm ${tierFilter === t ? 'btn-primary' : ''}`}
                  onClick={() => setTierFilter(t)}>
                  {t === 'all' ? 'All' : `${TIER_STYLE[t]?.dot || ''} ${t}`}
                </button>
              ))}
            </div>
            <button className="btn btn-sm btn-primary" onClick={() => setShowLinkModal(true)}>
              + Link sites
            </button>
          </div>

          {programSites.length === 0 && (
            <div className="empty-state">
              <div className="empty-state-icon">📍</div>
              <h3>No sites linked yet</h3>
              <p>Click <strong>+ Link sites</strong> above to add sites to this program.</p>
            </div>
          )}

          {filteredSites.map(ps => {
            const det = pickDetail(ps.sites?.site_details)
            const tier = det?.priority_tier || (slug === 'amp-migration' ? 'AMP' : null)
            const ts = tier ? TIER_STYLE[tier] : null
            return (
              <div key={ps.id} className="site-item"
                onClick={() => navigate(`/sites/${ps.sites?.id || ''}`)}>
                <div className="site-dot site-dot-partial" />
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <div className="site-name">{ps.sites?.name}</div>
                    {ts && (
                      <span style={{
                        fontSize: 11, padding: '2px 7px', borderRadius: 10,
                        background: ts.bg, color: ts.color, fontWeight: 500,
                      }}>
                        {ts.dot} {tier}
                      </span>
                    )}
                  </div>
                  <div className="site-meta">
                    {det?.district || ps.sites?.city}
                    {det?.priority_score && ` · Score ${det.priority_score}`}
                    {ps.hardware_cost_cents > 0 && ` · $${(ps.hardware_cost_cents/100).toLocaleString()} hw`}
                    {ps.hardware_cost_cents === 0 && slug === 'pa-phase1' && ` · $0 (already overhead)`}
                  </div>
                </div>
                {ps.hardware_install_status && ps.hardware_install_status !== 'not_required' && (
                  <span className={`badge ${ps.hardware_install_status === 'complete' ? 'badge-pass' : 'badge-warn'}`}>
                    {ps.hardware_install_status.replace('_',' ')}
                  </span>
                )}
                <button
                  className="btn btn-sm btn-danger"
                  onClick={(e) => { e.stopPropagation(); unlinkSite(ps.id, ps.sites?.name || 'this site') }}
                  title="Remove from this program">
                  Unlink
                </button>
              </div>
            )
          })}
        </>
      )}

      {tab === 'tickets' && (
        <>
          {tickets.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon">✅</div>
              <h3>No tickets</h3>
              <p>This program has no open tickets yet.</p>
            </div>
          ) : (
            tickets.map(t => {
              const ts = TIER_STYLE[t.tier]
              const ss = STATUS_STYLE[t.status] || STATUS_STYLE.open
              return (
                <div key={t.id} className="card"
                  style={{ marginBottom: 8, cursor: 'pointer' }}
                  onClick={() => navigate(`/tickets/${t.id}`)}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'start' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', marginBottom: 4 }}>
                        <span style={{ fontSize: 11, fontFamily: 'monospace', color: 'var(--text-muted)' }}>{t.ticket_number}</span>
                        {ts && (
                          <span style={{
                            fontSize: 11, padding: '2px 7px', borderRadius: 10,
                            background: ts.bg, color: ts.color, fontWeight: 500,
                          }}>{ts.dot} {t.tier}</span>
                        )}
                        {t.type && (
                          <span style={{ fontSize: 11, padding: '2px 7px', borderRadius: 10,
                            background: 'var(--gray-light)', color: 'var(--gray)', fontWeight: 500 }}>
                            {t.type}
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 2 }}>{t.title}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {t.sites?.name} · {t.assignee_team || 'Unassigned'}
                      </div>
                    </div>
                    <span style={{
                      fontSize: 11, padding: '3px 9px', borderRadius: 10,
                      background: ss.bg, color: ss.color, fontWeight: 500, flexShrink: 0,
                      textTransform: 'capitalize',
                    }}>
                      {t.status.replace('_',' ')}
                    </span>
                  </div>
                </div>
              )
            })
          )}
        </>
      )}

      {showLinkModal && (
        <LinkSitesModal
          allSites={allSites}
          linkedSiteIds={new Set(programSites.map(ps => ps.sites?.id).filter(Boolean))}
          onLink={linkSites}
          onClose={() => setShowLinkModal(false)}
        />
      )}
    </div>
  )
}

function formatDate(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function LinkSitesModal({ allSites, linkedSiteIds, onLink, onClose }) {
  const [selected, setSelected] = useState(new Set())
  const [search, setSearch] = useState('')
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [hardwareCost, setHardwareCost] = useState('') // dollars as string
  const [cutoverWave, setCutoverWave] = useState('')

  // Show only sites not already linked to this program.
  const candidates = allSites.filter(s => !linkedSiteIds.has(s.id))
  const q = search.trim().toLowerCase()
  const visible = q
    ? candidates.filter(s =>
        s.name.toLowerCase().includes(q) ||
        (s.city || '').toLowerCase().includes(q))
    : candidates

  function toggle(id) {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleAllVisible() {
    setSelected(prev => {
      const next = new Set(prev)
      const allOn = visible.every(s => next.has(s.id))
      if (allOn) visible.forEach(s => next.delete(s.id))
      else       visible.forEach(s => next.add(s.id))
      return next
    })
  }

  function handleLink() {
    const hwDollars = parseFloat(hardwareCost)
    const opts = {
      hardware_cost_cents: Number.isFinite(hwDollars) && hwDollars > 0 ? Math.round(hwDollars * 100) : 0,
      cutover_wave: cutoverWave ? parseInt(cutoverWave) : null,
    }
    onLink(Array.from(selected), opts)
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 560, maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-title">Link sites to this program</div>

        {candidates.length === 0 ? (
          <div style={{ padding: '16px 0', color: 'var(--text-muted)', fontSize: 14 }}>
            All active sites are already linked to this program.
          </div>
        ) : (
          <>
            <div className="form-group">
              <input
                className="form-input"
                placeholder="Search sites by name or city…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                autoFocus
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {selected.size} selected · {visible.length} shown · {candidates.length} available
              </div>
              {visible.length > 0 && (
                <button type="button" className="btn btn-sm" onClick={toggleAllVisible}>
                  {visible.every(s => selected.has(s.id)) ? 'Clear visible' : 'Select all visible'}
                </button>
              )}
            </div>

            <div style={{
              flex: 1, overflowY: 'auto', minHeight: 120, maxHeight: 280,
              border: '1px solid var(--border)', borderRadius: 6, padding: 4, marginBottom: 12,
            }}>
              {visible.length === 0 && (
                <div style={{ padding: 12, fontSize: 13, color: 'var(--text-muted)' }}>
                  No matching sites.
                </div>
              )}
              {visible.map(s => {
                const checked = selected.has(s.id)
                return (
                  <label key={s.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '6px 8px', borderRadius: 4, cursor: 'pointer',
                      background: checked ? 'var(--bg-secondary)' : 'transparent',
                    }}>
                    <input type="checkbox" checked={checked} onChange={() => toggle(s.id)} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 500 }}>{s.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                        {[s.city, s.state].filter(Boolean).join(', ') || '—'}
                      </div>
                    </div>
                  </label>
                )
              })}
            </div>

            <button
              type="button"
              onClick={() => setShowAdvanced(v => !v)}
              style={{
                background: 'none', border: 'none', padding: 0,
                color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer',
                marginBottom: showAdvanced ? 8 : 16, textAlign: 'left',
              }}>
              {showAdvanced ? '▾' : '▸'} Advanced (hardware cost, cutover wave)
            </button>

            {showAdvanced && (
              <div style={{ borderLeft: '2px solid var(--border)', paddingLeft: 12, marginBottom: 12 }}>
                <div className="form-group">
                  <label className="form-label">Hardware cost per site (USD)</label>
                  <input className="form-input" type="number" min="0" step="0.01"
                    value={hardwareCost}
                    onChange={e => setHardwareCost(e.target.value)}
                    placeholder="e.g. 2770.00" />
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                    Applied to every selected site. Leave blank or 0 for no hardware cost.
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">Cutover wave</label>
                  <input className="form-input" type="number" min="1"
                    value={cutoverWave}
                    onChange={e => setCutoverWave(e.target.value)}
                    placeholder="e.g. 1" />
                  <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                    Optional grouping for phased rollouts. Leave blank if not applicable.
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button
            className="btn btn-primary"
            disabled={selected.size === 0}
            onClick={handleLink}>
            Link {selected.size > 0 ? `${selected.size} site${selected.size === 1 ? '' : 's'}` : 'sites'}
          </button>
        </div>
      </div>
    </div>
  )
}
